/* eslint-disable react-native/no-inline-styles */
/**
 * ItemCart Component
 * 
 * Add-to-cart button with quantity controls
 * Features:
 * - Plus/minus buttons to adjust quantity
 * - Validates max items per cart and per item
 * - Handles multi-canteen cart conflicts (clears cart if different canteen)
 * - Shows confirmation dialog when switching canteens
 * 
 * @component
 * @param {Object} item - Menu item object with id, price, max_qty, etc.
 * @param {Object} canteen - Canteen object with settings
 */
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Dialog, IconButton, Portal, Text } from 'react-native-paper';
import { theme } from '../../theme/theme';
import { useDispatch, useSelector } from 'react-redux';
import { resetCart, setCartCanteen, updateCart } from '../../store/cart.slice';
import { showAlert } from '../../store/info.slice';

function ItemCart({ item, canteen }) {
    const dispatch = useDispatch();
    const canteen_id = item?.canteen_id;

    const enable_multi_canteens_orders = useSelector(state => state.customer?.customer?.enable_multi_canteens_orders);
    const cartCanteens = useSelector(state => state.cart.canteens);
    const _cart = useSelector(state => state.cart.canteens?.[canteen_id]?.cart);
    const totalCartItems = useSelector(state => state.cart?.totalCartItems);
    const cartCanteen = useSelector(state => state.cart.canteens?.[canteen_id]?.canteen);

    const cart = _cart || [];
    const cartItem = cart.find(_i => _i.item_id === item?.item_id);
    const quantity = cartItem?.item_qty || 0;
    const [visible, setVisible] = useState(false);

    /**
     * Clears existing cart and adds item from new canteen
     */
    const resetCartData = () => {
        setVisible(false);
        dispatch(resetCart());
        addCartItem();
    };

    /**
     * Validates if item can be added to cart
     * - Checks multi-canteen restrictions
     * - Validates max items per cart
     * - Validates max quantity per item
     * @returns {boolean} True if item can be added
     */
    const validateCartAction = () => {
        if (!enable_multi_canteens_orders && totalCartItems > 0 && !cartCanteens?.[canteen_id]) {
            setVisible(true);
            return false;
        }
        if (!!canteen?.settings?.max_cart_items && totalCartItems >= canteen?.settings?.max_cart_items) {
            dispatch(showAlert(`Maximum allowed Items are: ${canteen?.settings?.max_cart_items}`));
            return false;
        }

        if (cartItem && item?.max_qty > 0 && cartItem?.item_qty >= item?.max_qty) {
            dispatch(showAlert(`Maximum allowed quantity is: ${item?.max_qty}`));
            return false;
        }

        if (!cartCanteen) {
            dispatch(setCartCanteen({ canteen, canteen_id }));
        }

        return true;
    };

    const addCartItem = () => {
        dispatch(setCartCanteen({ canteen, canteen_id }));
        dispatch(updateCart({ canteen_id, item, type: 'inc' }));
    };

    const increaseQuantity = () => {
        if (!validateCartAction()) {
            return;
        }
        dispatch(updateCart({ canteen_id, item, type: 'inc' }));
    };

    const decreaseQuantity = () => {
        dispatch(updateCart({ canteen_id, item, type: 'dec' }));
    };

    return (
        <>
            {quantity > 0 ? (
                <>
                    <IconButton
                        icon="minus"
                        size={18}
                        iconColor={theme.colors.primary}
                        onPress={decreaseQuantity}
                        animated
                    />
                    <Text style={{ color: theme.colors.primary }}>{quantity}</Text>
                    <IconButton
                        icon="plus"
                        size={18}
                        iconColor={theme.colors.primary}
                        onPress={increaseQuantity}
                        animated
                    />
                </>
            ) : (
                <View style={styles.cartTextConatiner}>
                    <Text
                        onPress={increaseQuantity}
                        style={{
                            color: theme.colors.primary,
                            textTransform: 'uppercase',
                            textAlign: 'center',
                            width: '100%',
                        }}
                    >
                        add
                    </Text>
                </View>
            )}
            <Portal>
                <Dialog visible={visible} onDismiss={() => setVisible(false)}>
                    <Dialog.Title style={{ fontSize: 14 }}>Reset Cart?</Dialog.Title>
                    <Dialog.Content>
                        <Text variant="labelMedium">Do you wish to reset cart?</Text>
                    </Dialog.Content>
                    <Dialog.Actions>
                        <Button onPress={() => setVisible(false)}>No</Button>
                        <Button onPress={resetCartData}>Yes</Button>
                    </Dialog.Actions>
                </Dialog>
            </Portal>
        </>
    );
}

const styles = StyleSheet.create({
    cartTextConatiner: {
        alignItems: 'center',
        maxHeight: 32,
        paddingVertical: 4,
        minWidth: 98,
    },
});

export default ItemCart;